import ScreenRouteNames from "@/constants/screen-route-names";
import useMagicLink from "@/hooks/use-magic-link";
import useUserInfo from "@/hooks/use-user-info";
import type { Feature } from "@/model/features";
import type { RootState } from "@/store/store";
import { Loader2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIsOpen } from "@/store/chatbot/chatbot-slice";
import { queryClient } from "@/hooks/use-query-client";
import { QueryKeys } from "@/constants";
import type { GlobalFlag } from "@/model/global-flag";
import { normalizeUrl } from "@/lib/utils";

const FeatureListItem = ({
  keyValue,
  feature,
}: {
  keyValue: number;
  feature: Feature;
}) => {
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const globalFlag = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo?.username,
  ]);

  const magicLinkMutation = useMagicLink();
  const { isOpen } = useSelector((state: RootState) => state.chatbot);

  const handleFeatureClick = () => {
    if (feature?.action?.web_route) {
      if (
        feature?.action?.web_route === ScreenRouteNames.OLD_COST_ESTIMATOR.path
      ) {
        magicLinkMutation.mutate(userInfo);
      } else if (feature?.action?.web_route === ScreenRouteNames.CHAT.path) {
        if ((globalFlag as { data: GlobalFlag[] })?.data?.[0]?.showAIChat)
          dispatch(setIsOpen(!isOpen));
        else navigate("/not-found");
      } else {
        navigate(`/${feature.action.web_route}`);
      }
    }
    if (feature?.phone) {
      window.location.href = `tel:${feature.phone}`;
    }
    if (feature?.web_link) {
      const webLink = normalizeUrl(feature?.web_link);
      window.open(webLink, "_blank");
    }
  };

  return (
    /*
      2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. This was a <Card onClick> — Card
      renders a plain <div>, so every feature tile on the home page was unreachable by
      keyboard and announced as static text. A member navigating by keyboard could not open
      any feature from home. It is now a real button carrying Card's visual classes.
      The icon becomes alt="" because the tile's text already names it — otherwise the
      button's accessible name would repeat the feature name twice.
    */
    <button
      type="button"
      key={keyValue}
      onClick={handleFeatureClick}
      className="add-class flex h-[110px] w-[110px] cursor-pointer flex-col items-center justify-start gap-4 rounded-xl border-0 bg-card p-2 pt-5 text-card-foreground shadow-md hover:bg-primary-extraLight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {magicLinkMutation.isPending ? (
        <Loader2
          aria-hidden="true"
          className="size-8 animate-spin text-primary motion-reduce:animate-none"
        />
      ) : (
        <div className="aspect-square h-8">
          <img
            src={`data:image/svg+xml;base64,${feature.icon}`}
            alt=""
            className="h-full object-contain"
          />
        </div>
      )}

      {/* Was a <Label>, which is a <label> element — invalid inside a button, and
          orphaned since it labelled no control. */}
      <span className="overflow-hidden text-ellipsis text-center text-sm font-medium leading-none">
        {feature.name}
      </span>
    </button>
  );
};

export default FeatureListItem;
